import React from 'react';
import { usePrevious, useEventCallback } from '../../hooks';
import { DialogBackdropColor, useHoneycombTheme } from '../../theming';

import { Fade } from '../Fade';
import { StylerProps } from '../Styler';
import { STATIC_STYLES, useDynamicStyles } from './Dimmer.styles';
import { Loader, LoaderProps } from '../Loader';

export interface DimmerProps extends StylerProps {
    /**
     * If `true`, the dimmer will be visible.
     * @default false
     */
    active?: boolean;

    /**
     * The duration for the transition, in milliseconds.
     */
    transitionDuration?: { enter: number; exit: number };

    /**
     * The background color of the backdrop.
     * @default white
     */
    backgroundColor?: DialogBackdropColor;

    /**
     * Determines if a blur effect is applied to the backdrop.
     * @default false
     */
    blur?: boolean;

    /**
     * If `true`, a loading graphic will be displayed in the center of the dimmer.
     * @default false
     */
    loading?: boolean;

    /**
     * Additonal properties applied to the loader component.
     */
    LoaderProps?: LoaderProps;
}

export function Dimmer(props: Readonly<DimmerProps>): React.JSX.Element | null {
    const { theme } = useHoneycombTheme();
    const { transitions } = theme;

    const {
        active = false,
        transitionDuration = {
            enter: transitions.duration.shorter,
            exit: transitions.duration.shortest,
        },
        backgroundColor = 'white',
        blur: blurProp = false,
        loading = false,
        LoaderProps: LoaderPropsProp,
        ref,
        ...other
    } = props;

    const [exited, setExited] = React.useState(!active);
    const prevActive = usePrevious(active);

    React.useEffect(() => {
        if (!prevActive && active) {
            setExited(false);
        }
    }, [exited, prevActive, active]);

    const handleExited = useEventCallback(() => {
        setExited(true);
    });

    const cssVariables = useDynamicStyles(backgroundColor, blurProp);

    if (!active || exited) {
        return null;
    }

    return (
        <Fade in={active} timeout={transitionDuration} onExited={handleExited}>
            <div ref={ref} style={cssVariables} css={STATIC_STYLES} {...other}>
                {loading && (
                    // Loading spinner
                    <Loader pos="relative" size="xl" color="common.white" {...LoaderPropsProp} />
                )}
            </div>
        </Fade>
    );
}
